import { Input as UIKitInput } from "@credit-acceptance/uikit";
import { type ComponentProps, useLayoutEffect } from "react";

import { cn } from "../../../lib/utils";
import { useComposedInputReference } from "./useComposedInputReference";

/**
 * uikit `kind`s that are text inputs. Anything else (`number`, `date`, …) falls
 * back to `kind="text"` with the original `type` forwarded to the DOM node,
 * which uikit renders as `type={type ?? kind}`.
 */
const TEXT_KINDS = new Set(["text", "email", "tel", "url", "search", "password"]);

type UIKitTextKind = "text" | "email" | "tel" | "url" | "search" | "password";

/**
 * `size` is dropped: on `<input>` it is the character-width attribute, but uikit
 * reads it as the control's height variant, which this adapter pins to `small`.
 * `defaultValue` narrows to `string` — uikit's text inputs seed their internal
 * state from it, and `number`/`string[]` have no meaning there.
 */
type InputProperties = Omit<ComponentProps<"input">, "size" | "defaultValue"> & {
  defaultValue?: string;
};

/**
 * Drop-in replacement for the shadcn `ui/input`, rendering uikit's Input.
 *
 * `size="small"` resolves to `--input-small-height` = 24px, matching the `h-6`
 * the shadcn version used, so no form row changes height.
 */
function Input({ className, type = "text", value, onChange, ref, maxLength, ...properties }: InputProperties) {
  const innerReference = useComposedInputReference(ref);
  const kind = (TEXT_KINDS.has(type) ? type : "text") as UIKitTextKind;

  // `maxLength` is applied to the DOM node instead of handed to uikit, which
  // reads it as a guard that drops any change longer than the limit outright.
  // Callers here (SsnInput, PhoneNumberInput, PhoneRow) rely on the native
  // attribute's semantics: cap typed input, but still deliver an over-long
  // paste so their own handler can strip and truncate it. uikit also keeps it
  // off the element entirely, which the DOM-attribute assertions depend on.
  useLayoutEffect(() => {
    const node = innerReference.current;
    if (!node) {
      return;
    }
    if (maxLength === undefined) {
      node.removeAttribute("maxlength");
    } else {
      node.setAttribute("maxlength", String(maxLength));
    }
  }, [innerReference, maxLength]);

  const uikitProperties = {
    kind,
    size: "small",
    // uikit puts `min-w-0` on its inner wrapper but not on the outer element,
    // so as a grid/flex item the control cannot shrink below the `<input>`'s
    // intrinsic ~148px width and overflows narrow columns (the Street No. and
    // Apt. cells of the address row). The shadcn input carried `min-w-0` on the
    // element itself; restore it here. Caller classes still win — `cn` merges
    // them last.
    className: cn("min-w-0", className),
    innerRef: innerReference,
    // Drives uikit's `data-invalid` styling hooks. The DOM attribute itself is
    // re-applied from `properties` below so an absent `aria-invalid` stays
    // absent rather than rendering as "false".
    isError: properties["aria-invalid"] === true || properties["aria-invalid"] === "true",
    type: kind === "text" && type !== "text" ? type : undefined,
    value: value === undefined ? undefined : String(value),
    // uikit reports (newValue, oldValue, event); callers here — RHF fields and
    // hand-written digit filters alike — expect the native change event.
    onChange: (_next: string, _previous: string | undefined, event?: React.ChangeEvent<HTMLInputElement>) => {
      if (event) {
        onChange?.(event);
      }
    },
    ...properties,
    // uikit builds its input props from an older @types/react, so a handful of
    // DOM attributes (`formAction`, `popoverTarget`, …) are typed narrower than
    // this app's `ComponentProps<"input">`. They are passed through untouched;
    // the cast only reconciles the two type versions.
  } as ComponentProps<typeof UIKitInput>;

  return <UIKitInput {...uikitProperties} />;
}

export { Input };
